import { type Database } from "@fitness-app/supabase";

export type TeamMemberTaskEntity = Database["public"]["Tables"]["team_member_task"]["Row"];

export enum TeamMemberTaskStatus {
  Pending = "pending",
  InProgress = "in_progress",
  Completed = "completed",
  Canceled = "canceled",
}

export enum TeamMemberTaskPriority {
  Low = "low",
  Medium = "medium",
  High = "high",
  Urgent = "urgent",
}

export enum TeamMemberTaskCategory {
  TrainingPlan = "training_plan",
  NutritionPlan = "nutrition_plan",
  ClientCommunication = "client_communication",
  Measurements = "measurements",
  ProgressReview = "progress_review",
  ProgramRenewals = "program_renewals",
  Other = "other",
}

export enum RecurrenceIntervalUnit {
  Day = "day",
  Week = "week",
  Month = "month",
  Year = "year",
}

export interface RecurrenceInterval {
  value: number;
  unit: RecurrenceIntervalUnit;
}

export interface TeamMemberTaskReminder {
  id: string;
  date: string;
  sent: boolean;
}

export interface TaskDateTime {
  date: string | null; // YYYY-MM-DD
  time: string | null; // HH:mm
  timezone: string; // e.g., "Europe/Warsaw"
}

export interface TeamMemberTaskRecurrence {
  enabled: boolean;
  interval: RecurrenceInterval;
  endDate?: string;
  lastGeneratedAt?: string;
}

export interface TeamMemberTask extends Omit<
  TeamMemberTaskEntity,
  "metadata" | "recurrence" | "reminders" | "startDate" | "endDate" | "dueDate"
> {
  status: TeamMemberTaskStatus;
  priority: TeamMemberTaskPriority;
  category: TeamMemberTaskCategory;
  startDate: TaskDateTime | null;
  endDate: TaskDateTime | null;
  dueDate: TaskDateTime | null;
  completedAt: string | null;
  traineeId: string | null;
  assignedTeamMemberId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
  reminders: TeamMemberTaskReminder[] | null;
  recurrence: TeamMemberTaskRecurrence | null;
  metadata: TeamMemberTaskMetadata | null;
  tags: string[];
}

export interface TeamMemberTaskMetadata {
  notes?: string;
  linkedEntityId?: string;
  linkedEntityType?: "training_program" | "nutrition_plan" | "survey" | "message";
}

export interface CreateTeamMemberTaskDto {
  title: string;
  description?: string | null;
  priority?: TeamMemberTaskPriority;
  category?: TeamMemberTaskCategory;
  startDate?: TaskDateTime | null;
  endDate?: TaskDateTime | null;
  dueDate?: TaskDateTime | null;
  traineeId?: string | null;
  assignedTeamMemberId: string | null;
  reminders?: TeamMemberTaskReminder[] | null;
  recurrence?: TeamMemberTaskRecurrence | null;
  metadata?: TeamMemberTaskMetadata | null;
  tags?: string[];
}

export interface UpdateTeamMemberTaskDto {
  title?: string;
  description?: string | null;
  status?: TeamMemberTaskStatus;
  priority?: TeamMemberTaskPriority;
  category?: TeamMemberTaskCategory;
  startDate?: TaskDateTime | null;
  endDate?: TaskDateTime | null;
  dueDate?: TaskDateTime | null;
  traineeId?: string | null;
  reminders?: TeamMemberTaskReminder[] | null;
  recurrence?: TeamMemberTaskRecurrence | null;
  metadata?: TeamMemberTaskMetadata | null;
  tags?: string[];
}

export interface TeamMemberTaskWithTrainee extends TeamMemberTask {
  trainee: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

export interface TeamMemberTaskWithAssignedMember extends TeamMemberTask {
  assignedMember: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export type TeamMemberTaskWithRelations = TeamMemberTaskWithTrainee & TeamMemberTaskWithAssignedMember;

export interface TeamMemberTasksFilter {
  status?: TeamMemberTaskStatus | TeamMemberTaskStatus[];
  priority?: TeamMemberTaskPriority | TeamMemberTaskPriority[];
  category?: TeamMemberTaskCategory | TeamMemberTaskCategory[];
  traineeId?: string | null;
  assignedTeamMemberId?: string;
  startDateFrom?: string;
  startDateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  tags?: string[];
  search?: string;
}

export interface TeamMemberTaskStats {
  total: number;
  pending: number;
  inProgress: number;
  completed: number;
  overdue: number;
  dueToday: number;
  dueThisWeek: number;
}
